import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FileText, Receipt, ScrollText, Users, Truck, IdCard, Plus, PackageOpen, Navigation, CheckCircle2, Wallet } from "lucide-react";

const cards = [
  { key: "consignments", label: "Consignment Notes", icon: FileText, to: "/portal/consignments", color: "#0b5cc4" },
  { key: "bills", label: "Freight Bills", icon: Receipt, to: "/portal/bills", color: "#d4a017" },
  { key: "memos", label: "Freight Memos", icon: ScrollText, to: "/portal/memos", color: "#0b5cc4" },
  { key: "parties", label: "Parties", icon: Users, to: "/portal/masters/parties", color: "#4b5563" },
  { key: "trucks", label: "Trucks", icon: Truck, to: "/portal/masters/trucks", color: "#4b5563" },
  { key: "drivers", label: "Drivers", icon: IdCard, to: "/portal/masters/drivers", color: "#4b5563" },
];

const statusCards = [
  { key: "booked", label: "Booked", icon: PackageOpen, cls: "text-slate-600", bg: "bg-slate-100" },
  { key: "in_transit", label: "In Transit", icon: Navigation, cls: "text-amber-600", bg: "bg-amber-50" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, cls: "text-green-600", bg: "bg-green-50" },
];

export default function Dashboard() {
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Snapshot of your logistics operations.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/portal/consignments/new" className="btn-primary px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2" data-testid="quick-new-consignment">
            <Plus size={16}/> New Consignment
          </Link>
        </div>
      </div>

      <div className="mb-6">
        <div className="text-sm font-semibold text-slate-600 mb-3">Delivery Status &amp; Payments</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {statusCards.map(({ key, label, icon: Icon, cls, bg }) => (
            <Link to="/portal/consignments" key={key} className={`stat-card block ${bg}`} data-testid={`status-count-${key}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">{label}</span>
                <Icon size={22} className={cls} />
              </div>
              <div className={`text-4xl font-semibold mt-3 ${cls}`}>
                {stats.status_counts?.[key] ?? "—"}
              </div>
            </Link>
          ))}
          <Link to="/portal/payments" className="stat-card block bg-red-50" data-testid="status-count-outstanding">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Outstanding</span>
              <Wallet size={22} className="text-red-600" />
            </div>
            <div className="text-3xl font-semibold mt-3 text-red-600">
              {stats.outstanding != null ? `₹ ${Number(stats.outstanding).toLocaleString("en-IN")}` : "—"}
            </div>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map(({ key, label, icon: Icon, to, color }) => (
          <Link to={to} key={key} className="stat-card block" data-testid={`stat-${key}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{label}</span>
              <Icon size={22} style={{ color }}/>
            </div>
            <div className="text-4xl font-semibold mt-3" style={{ color }}>
              {stats[key] ?? "—"}
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="stat-card">
          <div className="text-sm text-slate-500">Quick actions</div>
          <div className="mt-4 flex flex-col gap-2">
            <Link to="/portal/consignments/new" className="text-[#0b5cc4] hover:underline text-sm" data-testid="qa-consignment">→ Create Consignment Note</Link>
            <Link to="/portal/bills/new" className="text-[#0b5cc4] hover:underline text-sm" data-testid="qa-bill">→ Create Freight Bill</Link>
            <Link to="/portal/memos/new" className="text-[#0b5cc4] hover:underline text-sm" data-testid="qa-memo">→ Create Freight Memo</Link>
          </div>
        </div>
        <div className="stat-card lg:col-span-2">
          <div className="text-sm text-slate-500">About JAI JAGDAMBA LOGISTICS</div>
          <p className="mt-3 text-slate-700 leading-relaxed text-sm">
            17/1, Lakshmi Narayan Mukherjee Lane, Room No.209, 2nd Floor, KOLKATA-700006<br/>
            GSTIN: 19AAHPO6792Q1ZJ · PAN: AAHPO6792Q · Contact: 9831997959
          </p>
        </div>
      </div>
    </div>
  );
}
